import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calculator as CalcIcon, X, ArrowCounterClockwise, Backspace } from '@phosphor-icons/react';
import { Button } from '../ui/button';

/** Floating calculator modal — accessible from the topbar.
 *  Pure client-side, no persistence. Supports keyboard input. */
export default function CalculatorModal({ open, onClose }) {
  const [display, setDisplay] = useState('0');
  const [previous, setPrevious] = useState(null);  // last value
  const [op, setOp] = useState(null);              // pending operator: + - * /
  const [overwrite, setOverwrite] = useState(true); // next digit overwrites display
  const [history, setHistory] = useState([]);

  const reset = () => {
    setDisplay('0');
    setPrevious(null);
    setOp(null);
    setOverwrite(true);
  };

  const inputDigit = useCallback((d) => {
    setDisplay((prev) => {
      if (overwrite) { setOverwrite(false); return d === '.' ? '0.' : d; }
      if (d === '.' && prev.includes('.')) return prev;
      if (prev === '0' && d !== '.') return d;
      return prev + d;
    });
  }, [overwrite]);

  const compute = useCallback((a, b, operator) => {
    const x = parseFloat(a), y = parseFloat(b);
    if (Number.isNaN(x) || Number.isNaN(y)) return b;
    switch (operator) {
      case '+': return (x + y).toString();
      case '-': return (x - y).toString();
      case '*': return (x * y).toString();
      case '/': return y === 0 ? 'Error' : (x / y).toString();
      default:  return b;
    }
  }, []);

  const handleOp = useCallback((nextOp) => {
    if (previous !== null && op && !overwrite) {
      const result = compute(previous, display, op);
      if (result === 'Error') {
        setDisplay('Error');
        setPrevious(null);
        setOp(null);
        setOverwrite(true);
        return;
      }
      setHistory((h) => [{ expr: `${previous} ${op} ${display}`, val: result }, ...h].slice(0, 10));
      setDisplay(result);
      setPrevious(result);
    } else {
      setPrevious(display);
    }
    setOp(nextOp);
    setOverwrite(true);
  }, [previous, op, overwrite, display, compute]);

  const handleEquals = useCallback(() => {
    if (previous !== null && op) {
      const result = compute(previous, display, op);
      setHistory((h) => [{ expr: `${previous} ${op} ${display}`, val: result }, ...h].slice(0, 10));
      setDisplay(result);
      setPrevious(null);
      setOp(null);
      setOverwrite(true);
    }
  }, [previous, op, display, compute]);

  const handleBackspace = () => {
    setDisplay((prev) => {
      if (overwrite || prev.length === 1 || (prev.length === 2 && prev.startsWith('-'))) return '0';
      return prev.slice(0, -1);
    });
  };

  const handlePercent = () => {
    setDisplay((prev) => {
      const v = parseFloat(prev);
      return Number.isNaN(v) ? prev : (v / 100).toString();
    });
  };

  const handleSign = () => {
    setDisplay((prev) => {
      if (prev === '0' || prev === 'Error') return prev;
      return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
    });
  };

  // Keyboard support
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') inputDigit(e.key);
      else if (e.key === '.') inputDigit('.');
      else if (['+', '-', '*', '/'].includes(e.key)) handleOp(e.key);
      else if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); handleEquals(); }
      else if (e.key === 'Backspace') handleBackspace();
      else if (e.key === 'Escape') onClose();
      else if (e.key === '%') handlePercent();
      else if (e.key.toLowerCase() === 'c') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputDigit, handleOp, handleEquals]);

  if (!open) return null;

  const fmt = (v) => {
    if (v === 'Error') return v;
    const n = parseFloat(v);
    if (Number.isNaN(n)) return v;
    if (Math.abs(n) >= 1e9) return n.toExponential(6);
    return v.length > 14 ? n.toPrecision(12) : v;
  };

  const Btn = ({ label, onClick, className = '', testid }) => (
    <Button type="button" onClick={onClick}
      className={`h-12 text-base font-semibold ${className}`}
      data-testid={testid}
    >{label}</Button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="calculator-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border bg-card shadow-2xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header (always visible at top) */}
        <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30 shrink-0">
          <div className="flex items-center gap-2">
            <CalcIcon size={18} weight="fill" className="text-[#d4a84b]" />
            <span className="font-semibold">Calculator</span>
          </div>
          <button onClick={onClose} aria-label="Close calculator"
            className="p-1.5 rounded hover:bg-foreground/10 text-foreground" data-testid="calc-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 min-h-0">
        {/* Display */}
        <div className="p-4 bg-[#0a0e1a] text-right text-white">
          {previous !== null && op && (
            <div className="text-xs text-[#d4a84b]/70 font-mono">{previous} {op}</div>
          )}
          <div className="text-3xl font-bold font-mono tracking-tight break-all" data-testid="calc-display">
            {fmt(display)}
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-4 gap-2 p-3 bg-card">
          <Btn label="C" onClick={reset} className="bg-rose-500/15 text-rose-600 hover:bg-rose-500/25" testid="calc-c" />
          <Btn label="±" onClick={handleSign} className="bg-muted hover:bg-muted/80 text-foreground" testid="calc-sign" />
          <Btn label="%" onClick={handlePercent} className="bg-muted hover:bg-muted/80 text-foreground" testid="calc-percent" />
          <Btn label={<Backspace size={18} weight="bold" />} onClick={handleBackspace} className="bg-muted hover:bg-muted/80 text-foreground" testid="calc-back" />

          {[7, 8, 9].map(n => <Btn key={n} label={n.toString()} onClick={() => inputDigit(n.toString())} className="bg-secondary hover:bg-secondary/80 text-secondary-foreground" testid={`calc-${n}`} />)}
          <Btn label="÷" onClick={() => handleOp('/')} className={`text-white ${op === '/' ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`} testid="calc-div" />

          {[4, 5, 6].map(n => <Btn key={n} label={n.toString()} onClick={() => inputDigit(n.toString())} className="bg-secondary hover:bg-secondary/80 text-secondary-foreground" testid={`calc-${n}`} />)}
          <Btn label="×" onClick={() => handleOp('*')} className={`text-white ${op === '*' ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`} testid="calc-mul" />

          {[1, 2, 3].map(n => <Btn key={n} label={n.toString()} onClick={() => inputDigit(n.toString())} className="bg-secondary hover:bg-secondary/80 text-secondary-foreground" testid={`calc-${n}`} />)}
          <Btn label="−" onClick={() => handleOp('-')} className={`text-white ${op === '-' ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`} testid="calc-sub" />

          <Btn label="0" onClick={() => inputDigit('0')} className="col-span-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground" testid="calc-0" />
          <Btn label="." onClick={() => inputDigit('.')} className="bg-secondary hover:bg-secondary/80 text-secondary-foreground" testid="calc-dot" />
          <Btn label="+" onClick={() => handleOp('+')} className={`text-white ${op === '+' ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'}`} testid="calc-add" />

          <Btn label="=" onClick={handleEquals} className="col-span-4 bg-[#d4a84b] hover:bg-[#e8bc5e] text-[#0a0a0a]" testid="calc-eq" />
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="px-4 py-3 border-t bg-muted/30" data-testid="calc-history">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">Recent</span>
              <button onClick={() => setHistory([])} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                <ArrowCounterClockwise size={11} /> Clear
              </button>
            </div>
            <ul className="space-y-1 text-xs font-mono">
              {history.map((h, i) => (
                <li key={i} className="flex justify-between items-center hover:bg-foreground/5 px-2 py-1 rounded cursor-pointer"
                  onClick={() => { setDisplay(h.val); setOverwrite(true); }}>
                  <span className="text-muted-foreground truncate">{h.expr}</span>
                  <span className="font-bold ml-3">= {h.val}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>

        {/* Sticky footer Close button (always visible even on tiny viewports) */}
        <div className="border-t bg-muted/30 px-3 py-2 shrink-0">
          <button onClick={onClose} data-testid="calc-close-bottom"
            className="w-full text-sm font-medium text-muted-foreground hover:text-foreground py-1.5 rounded hover:bg-foreground/5 transition">
            Close (Esc)
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
